<template>
  <div class="card mb-4">
    <div class="card-header">
      <div class="row flex-between-center">
        <div class="col-auto">
          <h5 class="fs-6 mb-0">
            Trend Flips & Breakouts<a href="https://youtu.be/6w3vaAUT3RE" target="_blank" title="Watch explainer video"><span class="fab fa-youtube" data-fa-transform="grow-2 right-5 down-1"></span></a>
          </h5>
        </div>
      </div>
    </div>
    <div class="card-body p-0">
      <div v-if="results.length > 0" class="table-responsive scrollbar">
        <table class="table table-hover table-sm table-striped fs-10 mb-0 overflow-hidden">
          <thead class="bg-200">
            <tr>
              <th v-if="detector === 'b2s'" class="text-900 align-middle white-space-nowrap sortable text-center" @click="sortTable('marketRank')">
                Rank <span v-html="getSortIcon('marketRank')"></span>
              </th>
              <th class="text-900 align-middle white-space-nowrap">
                Asset
              </th>
              <th class="text-900 align-middle white-space-nowrap">
                Symbol
              </th>
              <th class="text-900 align-middle white-space-nowrap text-center">
                Chart
              </th>
              <th class="text-900 align-middle white-space-nowrap sortable" @click="sortTable('breakoutDate')">
                Last Breakout <span v-html="getSortIcon('breakoutDate')"></span>
              </th>
              <th class="text-900 align-middle white-space-nowrap sortable" @click="sortTable('trendChanged')">
                Last Trend Flip <span v-html="getSortIcon('trendChanged')"></span>
              </th>
              <th class="text-900 align-middle white-space-nowrap sortable" @click="sortTable('trendTo')">
                Trend Flip <span v-html="getSortIcon('trendTo')"></span>
              </th>
              <th class="text-900 align-middle white-space-nowrap sortable" @click="sortTable('currentStatus')">
                Trend <span v-html="getSortIcon('currentStatus')"></span>
              </th>
              <th class="text-900 align-middle white-space-nowrap sortable" @click="sortTable('tradeDate')">
                Trend Start <span v-html="getSortIcon('tradeDate')"></span>
              </th>
              <th class="text-900 align-middle white-space-nowrap sortable" @click="sortTable('pnlPct')">
                Trend Start P&amp;L % <span v-html="getSortIcon('pnlPct')"></span>
              </th>
            </tr>
          </thead>
          <tbody class="list">
            <tr v-for="item in sortedResults" :key="item.symbol">
              <td v-if="detector === 'b2s'" class="py-2 pe-2 align-middle white-space-nowrap text-center">
                {{ item.marketRank != null ? item.marketRank : '-' }}
              </td>
              <td class="py-2 pe-2 align-middle white-space-nowrap">
                <span class="fw-bold">{{ item.symbolTitle }}</span>
              </td>
              <td class="py-2 pe-2 align-middle white-space-nowrap">
                <a :href="'https://www.tradingview.com/chart/?symbol=' + item.symbol" target="_blank" class="text-primary">{{ item.symbol }}</a>
              </td>
              <td :class="['py-2 pe-2 align-middle text-center chart-cell', { 'chart-cell-empty': !item.breakoutChartUrl }]"
                  @mouseenter="item.breakoutChartUrl && onChartHover($event, item)"
                  @mousemove="onChartMove"
                  @mouseleave="onChartLeave">
                <a v-if="item.breakoutChartUrl"
                   href="#"
                   class="chart-icon-link text-primary"
                   :aria-label="'Open chart for ' + item.symbol"
                   @click.prevent="openChartModal(item)"
                   @focus="onChartFocus($event, item)"
                   @blur="onChartLeave">
                  <span class="fas fa-chart-line"></span>
                </a>
              </td>
              <td class="py-2 pe-2 align-middle white-space-nowrap">
                <span v-if="item.breakoutDate" :title="breakoutTooltip(item)">
                  {{ item.breakoutDate }}
                </span>
                <span v-else class="text-muted">-</span>
              </td>
              <td class="py-2 pe-2_ align-middle white-space-nowrap">{{ item.trendChanged || '-' }}</td>
              <td class="py-2 pe-2 align-middle white-space-nowrap">
                <span class="fw-bold" :class="getTrendBadgeClass(item.trendTo)">{{ item.trendFrom }} → {{ item.trendTo }}</span>
              </td>
              <td class="py-2 pe-2 align-middle white-space-nowrap">
                <span :class="item.currentStatus === 'OPEN' ? 'text-success' : 'text-danger'">{{ (item.currentStatus == 'OPEN')? 'UPTREND' : 'DOWNTREND' }}</span>
              </td>
              <td class="py-2 pe-2 align-middle white-space-nowrap">{{ item.tradeDate || '-' }}</td>
              <td class="py-2 pe-2_ align-middle white-space-nowrap" :class="getPnlClass(item.pnlPct)">
                {{ formatPnl(item.pnlPct) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="text-center py-4 text-muted">
        No results for the selected filters.
      </div>
    </div>
    <div v-if="hoveredItem"
         class="breakout-chart-popover"
         :style="{ left: popoverX + 'px', top: popoverY + 'px' }">
      <img v-if="currentChartUrl" :src="currentChartUrl" alt="Chart" />
      <div v-else class="breakout-chart-loading">Loading chart…</div>
    </div>
    <div v-if="modalItem"
         class="chart-modal-backdrop"
         role="dialog"
         aria-modal="true"
         :aria-label="'Chart for ' + modalItem.symbol"
         @click.self="closeChartModal">
      <div class="chart-modal-content">
        <button type="button"
                ref="modalCloseBtn"
                class="chart-modal-close"
                aria-label="Close chart"
                @click="closeChartModal">
          <span class="fas fa-times"></span>
        </button>
        <img v-if="modalChartUrl"
             :src="modalChartUrl"
             :alt="'Chart for ' + modalItem.symbol"
             class="chart-modal-image" />
        <div v-else class="chart-modal-loading">Loading chart…</div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiFetch } from '@/api/apiFetch'

export default {
  name: 'TrendRadarTable',
  props: {
    results: {
      type: Array,
      required: true
    },
    runDate: {
      type: String,
      default: null
    },
    detector: {
      type: String,
      default: 'gc'
    }
  },
  data () {
    return {
      sortBy: 'trendChanged',
      sortDirection: 'desc',
      // Row currently being hovered. When non-null, the popover is rendered.
      hoveredItem: null,
      popoverX: 0,
      popoverY: 0,
      // Row whose chart is open in the modal (click-to-open). Independent
      // of `hoveredItem` — the modal is a committed view that stays open
      // until the user closes it.
      modalItem: null,
      modalChartUrl: null,
      // Maps breakoutChartPath -> blob URL. Charts are auth-gated so we
      // fetch with apiFetch, convert to blob, and reuse the object URL.
      chartBlobCache: {},
      currentChartUrl: null
    }
  },
  mounted () {
    // ESC closes the modal globally so the user can dismiss it without
    // hunting for the X button.
    this.handleEscKey = (e) => {
      if (e.key === 'Escape' && this.modalItem) {
        this.closeChartModal()
      }
    }
    window.addEventListener('keydown', this.handleEscKey)
  },
  beforeUnmount () {
    Object.values(this.chartBlobCache).forEach(url => URL.revokeObjectURL(url))
    if (this.handleEscKey) {
      window.removeEventListener('keydown', this.handleEscKey)
    }
  },
  watch: {
    // When the page's run date changes (e.g. user keeps the tab open across
    // a new daily run), chart paths may be the same string but the server
    // now serves different PNGs behind them. Drop cached blob URLs so the
    // next hover refetches.
    runDate (newDate, oldDate) {
      if (oldDate != null && newDate !== oldDate) {
        Object.values(this.chartBlobCache).forEach(url => URL.revokeObjectURL(url))
        this.chartBlobCache = {}
        this.currentChartUrl = null
        // If the modal was open across a daily-run rollover, the image
        // the user is staring at is now stale — close it.
        this.modalItem = null
        this.modalChartUrl = null
      }
    }
  },
  computed: {
    sortedResults () {
      const sorted = [...this.results]
      const field = this.sortBy
      const dir = this.sortDirection === 'asc' ? 1 : -1

      sorted.sort((a, b) => {
        let valA = a[field]
        let valB = b[field]

        // Handle nulls
        if (valA == null && valB == null) return 0
        if (valA == null) return 1
        if (valB == null) return -1

        // Numeric sort for pnlPct and marketRank (coerce in case the API
        // delivers them as strings — string "-" subtraction would NaN).
        if (field === 'pnlPct' || field === 'marketRank') {
          return (Number(valA) - Number(valB)) * dir
        }

        // String sort
        valA = String(valA).toLowerCase()
        valB = String(valB).toLowerCase()
        if (valA < valB) return -1 * dir
        if (valA > valB) return 1 * dir
        return 0
      })

      return sorted
    }
  },
  methods: {
    sortTable (field) {
      if (this.sortBy === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortBy = field
        this.sortDirection = 'asc'
      }
    },
    getSortIcon (field) {
      if (this.sortBy !== field) return ''
      return this.sortDirection === 'asc' ? '&#9650;' : '&#9660;'
    },
    getTrendBadgeClass (trend) {
      if (trend === 'Green') return 'badge bg-success'
      if (trend === 'Red') return 'badge bg-danger'
      return 'badge bg-secondary'
    },
    getPnlClass (pnl) {
      if (pnl == null) return 'text-muted'
      return pnl >= 0 ? 'text-success' : 'text-danger'
    },
    formatPnl (pnl) {
      if (pnl == null) return '-'
      const sign = pnl >= 0 ? '+' : ''
      return sign + pnl.toFixed(2) + '%'
    },
    // Tooltip text only — the icon itself is always the chart icon
    // regardless of state. Validated state is conveyed implicitly via the
    // trend column showing Green; no extra visual noise here.
    breakoutTooltip (item) {
      if (item.breakoutValidatedAt) return 'Validated ' + item.breakoutValidatedAt + ' (B2S confirmed)'
      if (item.breakoutInvalidatedAt) return 'Invalidated ' + item.breakoutInvalidatedAt
      return 'Active breakout'
    },
    async loadChartBlob (chartUrl) {
      if (this.chartBlobCache[chartUrl]) return this.chartBlobCache[chartUrl]
      const url = process.env.VUE_APP_API_URL + chartUrl
      const res = await apiFetch(url, { method: 'GET' })
      if (res.status !== 200) return null
      const blob = await res.blob()
      const blobUrl = URL.createObjectURL(blob)
      this.chartBlobCache[chartUrl] = blobUrl
      return blobUrl
    },
    async onChartHover (event, item) {
      this.hoveredItem = item
      this.positionPopover(event)
      await this.loadChartForItem(item)
    },
    async onChartFocus (event, item) {
      // Keyboard focus path: position the popover next to the focused
      // icon rather than at the (unset) mouse coordinates. Mirrors hover
      // behaviour otherwise so keyboard navigation surfaces the chart.
      this.hoveredItem = item
      this.positionPopoverNearElement(event.target)
      await this.loadChartForItem(item)
    },
    async loadChartForItem (item) {
      const chartUrl = item.breakoutChartUrl
      // Use cached value if available so the image renders immediately.
      this.currentChartUrl = this.chartBlobCache[chartUrl] || null
      const blobUrl = await this.loadChartBlob(chartUrl)
      // Guard against the user moving away before the fetch resolved.
      if (this.hoveredItem === item) {
        this.currentChartUrl = blobUrl
      }
    },
    onChartMove (event) {
      if (this.hoveredItem) this.positionPopover(event)
    },
    onChartLeave () {
      this.hoveredItem = null
      this.currentChartUrl = null
    },
    async openChartModal (item) {
      this.modalItem = item
      // The hover popover and the modal are mutually exclusive — the modal
      // covers the screen and the popover would render behind it. Drop the
      // hover state so we don't leak stale popover behavior on close.
      this.hoveredItem = null
      this.currentChartUrl = null
      const chartUrl = item.breakoutChartUrl
      this.modalChartUrl = this.chartBlobCache[chartUrl] || null
      const blobUrl = await this.loadChartBlob(chartUrl)
      // Guard: user may have closed (or opened a different) modal while
      // the blob was loading.
      if (this.modalItem === item) {
        this.modalChartUrl = blobUrl
      }
      // Move focus to the close button so keyboard users can dismiss
      // immediately and screen readers announce the dialog.
      this.$nextTick(() => {
        if (this.$refs.modalCloseBtn) this.$refs.modalCloseBtn.focus()
      })
    },
    closeChartModal () {
      this.modalItem = null
      this.modalChartUrl = null
    },
    positionPopoverNearElement (el) {
      // Place the popover to the right of the focused element, falling
      // back to the left if it would overflow the viewport.
      const popoverWidth = 720
      const popoverHeight = 420
      const margin = 16
      const rect = el.getBoundingClientRect()
      let x = rect.right + 8
      let y = rect.top
      if (x + popoverWidth + margin > window.innerWidth) {
        x = rect.left - popoverWidth - 8
      }
      if (y + popoverHeight + margin > window.innerHeight) {
        y = window.innerHeight - popoverHeight - margin
      }
      if (y < margin) y = margin
      this.popoverX = Math.max(margin, x)
      this.popoverY = y
    },
    positionPopover (event) {
      const popoverWidth = 720
      const popoverHeight = 420
      const margin = 16
      let x = event.clientX + 20
      let y = event.clientY + 20
      if (x + popoverWidth + margin > window.innerWidth) {
        x = event.clientX - popoverWidth - 20
      }
      if (y + popoverHeight + margin > window.innerHeight) {
        y = window.innerHeight - popoverHeight - margin
      }
      if (y < margin) y = margin
      this.popoverX = Math.max(margin, x)
      this.popoverY = y
    }
  }
}
</script>

<style scoped>
.sortable {
  cursor: pointer;
  user-select: none;
}
.sortable:hover {
  background-color: rgba(0, 0, 0, 0.05);
}
.table tr th:last-child {
  text-align: right;
}
.table tr td:last-child {
  text-align: right;
}
.chart-cell {
  cursor: pointer;
  /* Reserve a comfortable hover area even when the icon glyph is small,
     so the user has a generous mouse target. */
  min-width: 60px;
}
.chart-cell:hover .fa-chart-line {
  filter: brightness(1.2);
}
/* Applied when the row has no chart to show — the cell still occupies the
   column width (min-width above) but doesn't advertise a clickable affordance. */
.chart-cell-empty {
  cursor: default;
}
.chart-icon-link {
  text-decoration: none;
}
/* Keyboard focus ring on the icon link — visible only when focused via
   keyboard navigation (not on click), per :focus-visible semantics. */
.chart-icon-link:focus-visible {
  outline: 2px solid #0d6efd;
  outline-offset: 2px;
  border-radius: 2px;
}

/* --- Click-to-open chart modal --------------------------------------- */
/* Backdrop covers the viewport; flexbox centers the dialog. z-index sits
   above the hover popover (1080) so a stale popover never leaks through. */
.chart-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  z-index: 1090;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.chart-modal-content {
  position: relative;
  max-width: 95vw;
  max-height: 95vh;
  background: #ffffff;
  border-radius: 6px;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.4);
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.chart-modal-image {
  display: block;
  max-width: 100%;
  max-height: calc(95vh - 32px);
  width: auto;
  height: auto;
  border-radius: 4px;
}
.chart-modal-loading {
  width: min(720px, 80vw);
  padding: 80px 0;
  text-align: center;
  color: #888;
  font-size: 14px;
}
/* Close button sits in the top-right corner of the dialog, slightly
   overhanging so it's visible even when the image fills the content area.
   44 x 44 hits the WCAG touch-target size on mobile. */
.chart-modal-close {
  position: absolute;
  top: -18px;
  right: -18px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.15);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  color: #333;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.chart-modal-close:hover {
  background: #f5f5f5;
}
.chart-modal-close:focus-visible {
  outline: 2px solid #0d6efd;
  outline-offset: 2px;
}

/* Mobile / narrow viewports: shrink padding so the chart uses more of
   the screen, and right-size the close button so it doesn't dominate the
   compact modal. 28 × 28 still clears Material's 24 × 24 touch-target
   floor but reads as proportional next to a width-constrained chart. */
@media (max-width: 600px) {
  .chart-modal-backdrop {
    padding: 8px;
  }
  .chart-modal-content {
    max-width: 100vw;
    max-height: 100vh;
  }
  .chart-modal-image {
    max-height: calc(100vh - 24px);
  }
  .chart-modal-close {
    top: 4px;
    right: 4px;
    width: 28px;
    height: 28px;
    font-size: 13px;
  }
}
.breakout-chart-popover {
  position: fixed;
  z-index: 1080;
  pointer-events: none;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  padding: 4px;
}
.breakout-chart-popover img {
  display: block;
  width: 720px;
  max-width: calc(100vw - 32px);
  height: auto;
  border-radius: 4px;
}
.breakout-chart-loading {
  width: 720px;
  max-width: calc(100vw - 32px);
  padding: 60px 0;
  text-align: center;
  color: #888;
  font-size: 12px;
}
</style>
